/**
 * MessageItemCard - Card de item da sequência (layout Studio com Timeline)
 *
 * Renderiza cada item da timeline em tema escuro com:
 * - Nó numerado circular à esquerda (ligado à spine vertical)
 * - Bloco de conteúdo escuro (zinc-950) com header tipo + ações
 * - Preview do conteúdo (texto/mídia/delay/contato/botões)
 * - Botões EDIT (pill) e remover
 */

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  GripVertical,
  Pencil,
  X,
  Type,
  Image,
  Video,
  Music,
  FileText,
  Timer,
  Circle,
  Film,
  MousePointerClick,
  User,
} from 'lucide-react';
import { MessageItem, CampaignItemType, itemTypeConfig } from './types';
import { useMediaGallery } from '@/hooks/useMediaGallery';

// =====================================================
// INTERFACES
// =====================================================

interface MessageItemCardProps {
  item: MessageItem;
  index: number;
  onEdit: () => void;
  onRemove: () => void;
  tenantId?: string;
}

// Mapeamento de ícones por tipo
const typeIcons: Record<CampaignItemType, React.ElementType> = {
  text: Type,
  image: Image,
  video: Video,
  audio: Music,
  ptv: Circle,
  gif: Film,
  document: FileText,
  delay: Timer,
  image_buttons: MousePointerClick,
  contact: User,
  template: FileText,
};

// Paleta semântica por tipo (mantém identidade visual consistente com o dock)
const typeAccent: Record<CampaignItemType, { text: string; bg: string; border: string; ring: string }> = {
  text:          { text: 'text-indigo-400',  bg: 'bg-indigo-500/10',  border: 'border-indigo-500',  ring: 'shadow-[0_0_15px_rgba(99,102,241,0.3)]' },
  image:         { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500', ring: 'shadow-[0_0_15px_rgba(16,185,129,0.3)]' },
  video:         { text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500',   ring: 'shadow-[0_0_15px_rgba(245,158,11,0.3)]' },
  audio:         { text: 'text-orange-400',  bg: 'bg-orange-500/10',  border: 'border-orange-500',  ring: 'shadow-[0_0_15px_rgba(249,115,22,0.3)]' },
  ptv:           { text: 'text-pink-400',    bg: 'bg-pink-500/10',    border: 'border-pink-500',    ring: 'shadow-[0_0_15px_rgba(236,72,153,0.3)]' },
  gif:           { text: 'text-cyan-400',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500',    ring: 'shadow-[0_0_15px_rgba(6,182,212,0.3)]' },
  document:      { text: 'text-yellow-400',  bg: 'bg-yellow-500/10',  border: 'border-yellow-500',  ring: 'shadow-[0_0_15px_rgba(234,179,8,0.3)]' },
  delay:         { text: 'text-zinc-300',    bg: 'bg-zinc-500/10',    border: 'border-zinc-500',    ring: 'shadow-[0_0_15px_rgba(113,113,122,0.3)]' },
  image_buttons: { text: 'text-violet-400',  bg: 'bg-violet-500/10',  border: 'border-violet-500',  ring: 'shadow-[0_0_15px_rgba(139,92,246,0.3)]' },
  contact:       { text: 'text-lime-400',    bg: 'bg-lime-500/10',    border: 'border-lime-500',    ring: 'shadow-[0_0_15px_rgba(132,204,22,0.3)]' },
  template:      { text: 'text-indigo-400',  bg: 'bg-indigo-500/10',  border: 'border-indigo-500',  ring: 'shadow-[0_0_15px_rgba(99,102,241,0.3)]' },
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function MessageItemCard({
  item,
  index,
  onEdit,
  onRemove,
  tenantId,
}: MessageItemCardProps) {
  const { getMediaUrl } = useMediaGallery({});
  const Icon = typeIcons[item.item_type];
  const config = itemTypeConfig[item.item_type];
  const accent = typeAccent[item.item_type];

  // Número formatado com zero à esquerda (01, 02, ...)
  const orderLabel = String(index + 1).padStart(2, '0');

  // ---------------------------------------------------
  // Preview do conteúdo (mesma lógica, tema escuro)
  // ---------------------------------------------------
  const renderContent = () => {
    switch (item.item_type) {
      case 'text':
        return (
          <p className="text-sm text-zinc-700 leading-relaxed line-clamp-3 whitespace-pre-wrap">
            {item.text_content || 'Texto vazio'}
          </p>
        );

      case 'delay':
        return (
          <p className="text-sm text-zinc-700">
            Aguardar <strong className="text-zinc-900">{item.delay_seconds || 0}</strong> segundos
          </p>
        );

      case 'image':
      case 'gif': {
        if (!item.media_url) {
          return <p className="text-sm text-zinc-400">Nenhuma imagem selecionada</p>;
        }
        const url = item.media_url.startsWith('http') ? item.media_url : getMediaUrl(item.media_url);
        return (
          <div className="flex gap-3">
            <img
              src={url || item.media_url}
              alt={item.media_filename || 'Imagem'}
              className="w-16 h-16 object-cover rounded-md border border-zinc-200"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">
                {item.media_filename || 'Imagem'}
              </p>
              {item.text_content && (
                <p className="text-xs text-zinc-400 line-clamp-2 mt-1">
                  Legenda: {item.text_content}
                </p>
              )}
            </div>
          </div>
        );
      }

      case 'video':
      case 'ptv':
        return (
          <div className="flex gap-3">
            <div className="w-16 h-16 bg-white border border-zinc-200 rounded-md flex items-center justify-center">
              <Video className="h-8 w-8 text-zinc-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">
                {item.media_filename || (item.item_type === 'ptv' ? 'Vídeo Circular' : 'Vídeo')}
              </p>
              {item.text_content && (
                <p className="text-xs text-zinc-400 line-clamp-2 mt-1">
                  Legenda: {item.text_content}
                </p>
              )}
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="flex gap-3 items-center">
            <div className="w-12 h-12 bg-white border border-zinc-200 rounded-md flex items-center justify-center">
              <Music className="h-6 w-6 text-zinc-400" />
            </div>
            <p className="text-sm font-medium text-zinc-900 truncate flex-1">
              {item.media_filename || 'Áudio'}
            </p>
          </div>
        );

      case 'document':
        return (
          <div className="flex gap-3 items-center">
            <div className="w-12 h-12 bg-white border border-zinc-200 rounded-md flex items-center justify-center">
              <FileText className="h-6 w-6 text-zinc-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">
                {item.media_filename || 'Documento'}
              </p>
              {item.text_content && (
                <p className="text-xs text-zinc-400 line-clamp-2 mt-1">
                  Legenda: {item.text_content}
                </p>
              )}
            </div>
          </div>
        );

      case 'image_buttons': {
        if (!item.media_url) {
          return <p className="text-sm text-zinc-400">Nenhuma imagem selecionada</p>;
        }
        const url = item.media_url.startsWith('http') ? item.media_url : getMediaUrl(item.media_url);
        return (
          <div className="space-y-2">
            <div className="flex gap-3">
              <img
                src={url || item.media_url}
                alt={item.media_filename || 'Imagem'}
                className="w-16 h-16 object-cover rounded-md border border-zinc-200"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 truncate">
                  {item.media_filename || 'Imagem com Botões'}
                </p>
                {item.text_content && (
                  <p className="text-xs text-zinc-400 line-clamp-2 mt-1">{item.text_content}</p>
                )}
              </div>
            </div>
            {item.buttons_config?.buttons && item.buttons_config.buttons.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {item.buttons_config.buttons.map((btn, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="text-xs border-zinc-200 bg-white text-zinc-600"
                  >
                    {btn.label}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'contact':
        return (
          <div className="flex gap-3 items-center">
            <div className="w-12 h-12 bg-white border border-zinc-200 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-zinc-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">
                {item.contact_name || 'Contato'}
              </p>
              <p className="text-xs text-zinc-400 truncate">{item.contact_phone}</p>
              {item.contact_business_description && (
                <p className="text-xs text-zinc-500 truncate">
                  {item.contact_business_description}
                </p>
              )}
            </div>
          </div>
        );

      default:
        return <p className="text-sm text-zinc-400">Item desconhecido</p>;
    }
  };

  // ---------------------------------------------------
  // Render — nó numerado + bloco de conteúdo
  // ---------------------------------------------------
  return (
    <div className="relative flex items-start gap-6 group">
      {/* Coluna do nó (alinhada à spine vertical do canvas) */}
      <div className="z-10 flex flex-col items-center shrink-0">
        {/* Nó circular numerado com glow */}
        <div
          className={`w-8 h-8 rounded-full bg-white border-2 ${accent.border} flex items-center justify-center text-[11px] font-bold ${accent.text}`}
        >
          {orderLabel}
        </div>
        {/* Handle de drag */}
        <div className="mt-2 cursor-grab active:cursor-grabbing p-1 text-zinc-400 hover:text-zinc-600 transition-colors">
          <GripVertical className="h-4 w-4" />
        </div>
      </div>

      {/* Bloco de conteúdo */}
      <div className="flex-1 min-w-0 bg-white border border-zinc-200 rounded-xl p-5 shadow-lg hover:border-zinc-300 transition-all">
        {/* Header interno: ícone+tipo / ações */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 ${accent.bg} ${accent.text} rounded-lg shrink-0`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h4 className="text-xs font-bold text-zinc-600 uppercase tracking-widest truncate">
                {config?.label || 'Conteúdo'}
              </h4>
              <p className="text-[11px] text-zinc-400 mt-0.5">Bloco #{orderLabel}</p>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onEdit}
              className="h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider bg-zinc-50 border border-zinc-200 text-zinc-500 hover:text-indigo-600 hover:bg-white hover:border-indigo-500/30"
            >
              <Pencil className="w-3 h-3 mr-1" />
              Editar
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onRemove}
              className="h-7 w-7 text-zinc-400 hover:text-rose-500 hover:bg-rose-50"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Corpo: preview do item */}
        <div className="bg-zinc-50 p-4 rounded-lg border border-zinc-200">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
